<script lang="ts">
	import type { Template, DomApi } from 'types/common'
	import type { Column } from '@isoftdata/svelte-table'
	import type { AdditionalInfoType } from './ItemDetail.svelte'
	import type { Vehicle } from './results.ts'
	import type { Breakpoints } from '@isoftdata/utility-bootstrap'
	import type { AbstractStateRouter } from 'abstract-state-router'
	import type { VehicleSearchResultsData$result } from '$houdini'
	import type { PerPageCount } from 'utility/search-fields'
	import type { i18n } from 'types/common'

	import { localWritable } from '@macfja/svelte-persistent-store'
	import { tick, onMount } from 'svelte'
	import { fly } from 'svelte/transition'
	import Modal from '@isoftdata/svelte-modal'
	import ItemDetail from './ItemDetail.svelte'
	import Select from '@isoftdata/svelte-select'
	import Button from '@isoftdata/svelte-button'
	import Checkbox from '@isoftdata/svelte-checkbox'
	import { Table, Td } from '@isoftdata/svelte-table'
	import ImageThumbnail from '@isoftdata/svelte-image-thumbnail'
	import { getBootstrapBreakpoint } from '@isoftdata/utility-bootstrap'
	import { format as formatCurrency } from '@isoftdata/utility-currency'
	import { perPageCounts, computeTypeLabelColumns } from 'utility/search-fields'
	import { getContext } from 'svelte'

	type Vehicles = VehicleSearchResultsData$result['vehicles']

	interface Props {
		sortColumnProp: string
		pageNumber: number // Required
		vehicleSearchResults: Vehicles // Required
		sortDirection?: 'ASC' | 'DESC'
		perPageCount: PerPageCount // Required
		asr: AbstractStateRouter<Template, DomApi>
		selectedVehicleId: number | null // Required
		previousSortDirection?: 'ASC' | 'DESC'
		unitTypeId: number | undefined
	}

	let {
		sortColumnProp,
		pageNumber,
		vehicleSearchResults,
		sortDirection = 'ASC',
		perPageCount = $bindable(),
		asr,
		selectedVehicleId,
		previousSortDirection = $bindable('DESC'),
		unitTypeId,
	}: Props = $props()

	let itemDetailVehicle: Vehicle | null = $state(null)
	let bootstrapBreakpoint: Breakpoints | 'xxl' = $state(getBootstrapBreakpoint()) //it can't actually be xxl class, but because of the typing of the getBootstrapBreakpoint fn, we need to allow xxl
	let showItemDetailModal: boolean = $state(false)
	let itemDetailAdditionalInfoType: AdditionalInfoType = $state('Basic Info')

	const { t: translate } = getContext<i18n>('i18next')

	const showThumbnails = localWritable('showThumbs', true)
	// svelte-ignore state_referenced_locally
	let sortColumn: Column | undefined = sortColumnProp
		? computeTableColumns($showThumbnails).find(column => column.property === sortColumnProp)
		: undefined

	function computeItemDetailManModSubtitle(vehicle: Vehicle | null): string {
		if (vehicle) {
			const { manufacturer, model, year } = vehicle
			if (manufacturer && model) {
				return [year, manufacturer.name, model.name].filter(v => v).join(' > ')
			}

			return vehicle.stockNumber ?? ''
		}

		return ''
	}

	function computeTableColumns(showThumbnails: boolean, typeLabelColumns: Array<Column> = []) {
		let columns: Array<Column> = [
			{ property: 'attentionIcons', name: '', icon: 'circle-info', sortType: false, minWidth: '150px' },
			{ property: 'storeId', name: translate('common:store', 'Store'), numeric: true },
			{ property: 'stockNumber', name: translate('common:stockNum', 'Stock #') },
			{ property: 'wholeUnit[inventoryType][name]', name: translate('common:typeName', 'Type Name') },
			{ property: 'year', name: translate('common:year', 'Year'), numeric: true },
			{ property: 'make', name: translate('common:make', 'Make') },
			{ property: 'model', name: translate('common:model', 'Model') },
			{ property: 'vin', name: translate('common:vin', 'VIN') },
			{ property: 'categoryType[name]', name: translate('common:category', 'Category') },
			{ property: 'location', name: translate('common:location', 'Location') },
			{ property: 'receivedDate', name: translate('common:dateReceived', 'Date Received') },
			{ property: 'status', name: translate('common:status', 'Status') },
			{ property: 'userStatus', name: translate('common:userStatus', 'User Status') },
			{ property: 'wholeUnit[retailPrice]', name: translate('common:retail', 'Retail'), numeric: true },
		]

		columns = [...columns, ...typeLabelColumns]

		if (showThumbnails) {
			const attentionColumnIndex = columns.findIndex(column => (column.property = 'attentionIcons'))
			if (attentionColumnIndex > -1) {
				columns.splice(attentionColumnIndex + 1, 0, { property: 'attachmentCount', icon: 'photo-video', name: '' })
			}
		}

		return columns
	}

	async function showDetailPane(item: Vehicle, additionalInfoType: AdditionalInfoType) {
		if (item.id === itemDetailVehicle?.id && additionalInfoType === itemDetailAdditionalInfoType) {
			//They clicked the same button, so just close the sidebar
			itemDetailVehicle = null
			additionalInfoType = 'Basic Info'
			return
		}

		itemDetailVehicle = item
		if (isPhoneSize) {
			showItemDetailModal = true
		}
		await tick()
		if (additionalInfoType) {
			itemDetailAdditionalInfoType = additionalInfoType
		}
	}

	const itemDetailManModSubtitle: string = $derived(computeItemDetailManModSubtitle(itemDetailVehicle))
	const isPhoneSize: boolean = $derived(bootstrapBreakpoint === 'xs')
	const typeLabelColumns = $derived(
		computeTypeLabelColumns(
			unitTypeId,
			vehicleSearchResults.items.map(item => item.wholeUnit?.inventoryType),
		),
	)
	const resultsColumns: Array<Column> = $derived(computeTableColumns($showThumbnails, typeLabelColumns))
</script>

<svelte:window
	onresize={() => {
		bootstrapBreakpoint = getBootstrapBreakpoint()
		if (!isPhoneSize && showItemDetailModal) {
			showItemDetailModal = false
		} else if (isPhoneSize && itemDetailVehicle) {
			showItemDetailModal = true
		}
	}}
/>

<div class="card-body">
	<div class="row justify-content-between align-items-end mt-3 mx-1">
		<div class="col-auto">
			<Checkbox
				inline
				label={translate('common:showImageThumbnails', 'Show Image Thumbnails')}
				bind:checked={$showThumbnails}
			/>
		</div>
		<div class="col-auto mb-1">
			{translate('vehicleSearch.vehiclesFound', '{{count}} Vehicles Found', { count: vehicleSearchResults.totalItems })}
		</div>
		<div class="col-auto">
			<Select
				label={translate('common:resultsPerPage', 'Results Per Page')}
				labelClass="me-2 text-nowrap"
				labelParentClass="d-flex align-items-center mb-0"
				showEmptyOption={false}
				bind:value={perPageCount}
				onchange={() => {
					localStorage.setItem('vehicleSearchResultsPerPageCount', perPageCount)
					asr.go(null, { perPageCount, pageNumber: 1 }, { inherit: true })
				}}
			>
				{#each perPageCounts as perPageCount}
					<option value={perPageCount}>{perPageCount}</option>
				{/each}
			</Select>
		</div>
		{#if itemDetailVehicle && !isPhoneSize}
			<div class="col-md-6 col-lg-4 col-xl-3"></div>
		{/if}
	</div>

	<div class="row flex-nowrap">
		<div class={itemDetailVehicle && !isPhoneSize ? 'col-md-6 col-lg-8' : 'col-auto'}>
			<Table
				parentStyle="overflow-y: auto; max-height: 85vh;"
				responsive
				stickyHeader
				columnHidingEnabled
				{previousSortDirection}
				{sortDirection}
				{sortColumn}
				idProp="id"
				rows={vehicleSearchResults.items}
				columns={resultsColumns}
				currentPageNumber={pageNumber}
				totalItemsCount={vehicleSearchResults.totalItems}
				selectedRowIds={[selectedVehicleId]}
				perPageCount={parseInt(perPageCount, 10)}
				pageChange={({ pageNumber }) => asr.go(null, { pageNumber: pageNumber }, { inherit: true })}
				columnClickedMethod={async (column, direction) => {
					previousSortDirection = sortDirection === 'ASC' ? 'DESC' : 'ASC'
					asr.go(null, { sortColumn: column.property, sortDirection: direction, pageNumber: 1 }, { inherit: true })
				}}
			>
				{#snippet children({ row: vehicle })}
					<tr
						class="cursor-pointer"
						class:table-primary={selectedVehicleId === vehicle.id}
						onclick={() => asr.go('app.vehicle', { vehicleId: vehicle.id, storeId: vehicle.storeId })}
					>
						<Td
							stopPropagation
							property="attentionIcons"
						>
							<Button
								size="xs"
								color="secondary"
								outline={!(itemDetailVehicle?.id === vehicle.id && itemDetailAdditionalInfoType === 'Basic Info')}
								iconClass="truck"
								title={translate('vehicleSearch.viewVehicleInfo', 'View Vehicle Info')}
								onclick={() => {
									showDetailPane(vehicle, 'Basic Info')
								}}
							/>
							{#if vehicle.componentCount > 0}
								<Button
									size="xs"
									color="secondary"
									outline={!(itemDetailVehicle?.id === vehicle.id && itemDetailAdditionalInfoType === 'Components')}
									iconClass="engine"
									title={translate('vehicleSearch.viewVehicleComponents', 'View Vehicle Components')}
									onclick={() => {
										showDetailPane(vehicle, 'Components')
									}}>{vehicle.componentCount}</Button
								>
							{/if}
							<!-- {#if vehicle.messageCount}
							<Button
								size="xs"
								color="dark"
								outline={!(itemDetailVehicle?.id === vehicle.id && itemDetailAdditionalInfoType === 'Messages')}
								iconClass="message-lines"
								title="View Vehicle Messages"
								on:click={() => {
									showDetailPane(vehicle, 'Messages')
								}}>{vehicle.messageCount}</Button
							>
						{/if} -->
							<!-- <Button
							size="xs"
							color="dark"
							style="width: 46px;"
							outline={!(itemDetailVehicle?.id === vehicle.id && itemDetailAdditionalInfoType === 'Allocations')}
							iconClass="files"
							title={inventoryTotalAllocationsMap.get(vehicle.id) ? 'View Part Allocations' : 'No Allocations for This Part'}
							colorGreyDisabled={false}
							disabled={$inventoryAllocationsQuery.fetching || !inventoryTotalAllocationsMap.has(vehicle.id) || !inventoryTotalAllocationsMap.get(vehicle.id)}
							on:click={() => {
								showDetailPane(vehicle, 'Allocations')
							}}>{$inventoryAllocationsQuery.fetching ? '' : inventoryTotalAllocationsMap.get(vehicle.id) ?? '0'}</Button
						> -->
							{#if vehicle.attachments.length}
								<Button
									size="xs"
									color="secondary"
									outline={!(itemDetailVehicle?.id === vehicle.id && itemDetailAdditionalInfoType === 'Attachments')}
									iconClass="paperclip"
									title={translate('vehicleSearch.viewPartAttachments', 'View Part Attachments')}
									onclick={() => {
										showDetailPane(vehicle, 'Attachments')
									}}>{vehicle.attachments.length}</Button
								>
							{/if}
						</Td>
						{#if $showThumbnails}
							<Td
								stopPropagation
								property="attachmentCount"
							>
								<ImageThumbnail
									showThumbnail
									showImageCount
									noImagePath="images/noimage.jpg"
									thumbnailFile={{ path: vehicle?.topImage?.path ?? '' }}
									context={vehicle.attachments}
									fileCount={vehicle.attachments.filter(attachment => attachment.file.mimetype.startsWith('image/'))
										.length}
									click={() => showDetailPane(vehicle, 'Attachments')}
								/>
							</Td>
						{/if}
						<Td property="storeId">{vehicle.storeId ?? ''}</Td>
						<Td property="stockNumber">{vehicle.stockNumber ?? ''}</Td>
						<Td property="wholeUnit[inventoryType][name]">{vehicle.wholeUnit?.inventoryType?.name ?? ''}</Td>
						<Td property="year">{vehicle.year ?? ''}</Td>
						<Td property="make">{vehicle.manufacturer?.name ?? ''}</Td>
						<Td property="model">{vehicle.model?.name ?? ''}</Td>
						<Td property="vin">{vehicle.vin ?? ''}</Td>
						<Td property="categoryType[name]">{vehicle.categoryType?.name ?? ''}</Td>
						<Td property="location">{vehicle.location ?? ''}</Td>
						<Td property="receivedDate">{vehicle.receivedDate ? vehicle.receivedDate.toLocaleDateString() : ''}</Td>
						<Td property="status">{vehicle.status ?? ''}</Td>
						<Td property="userStatus">{vehicle.userStatus ?? ''}</Td>
						<Td property="wholeUnit[retailPrice]">{formatCurrency(vehicle.wholeUnit?.retailPrice ?? '')}</Td>
						{#each typeLabelColumns as column}
							<Td property={column.property}>
								{#if !unitTypeId && vehicle[column.property]?.data}
									<span
										class="fw-bold"
										style="white-space: nowrap;"
										>{vehicle[column.property.replace('Label', 'Field')]?.label ?? ''}:</span
									>
								{/if}
								{vehicle[column.property]?.data ?? ''}
							</Td>
						{/each}
					</tr>
				{/snippet}
				{#snippet noRows({ visibleColumnsCount })}
					<tr>
						<td colspan={visibleColumnsCount}>
							{translate('vehicleSearch.noMatchingVehiclesFound', 'No matching vehicles found.')}</td
						>
					</tr>
				{/snippet}
			</Table>
		</div>
		{#if itemDetailVehicle && !isPhoneSize}
			<div class="col-md-6 col-lg-4">
				<div
					class="card"
					transition:fly={{ x: 500, duration: 200 }}
				>
					<div class="card-header">
						<div class="d-flex justify-content-between align-items-center flex-nowrap">
							<h5 class="w-100 text-truncate">
								{#if itemDetailVehicle}
									{translate('common:stockNum', 'Stock #')}: {itemDetailVehicle?.stockNumber ?? ''}
									<br />
									<small>
										<span class="fw-bold">{itemDetailVehicle?.wholeUnit?.inventoryType?.name}</span>
										{itemDetailManModSubtitle}
									</small>
								{/if}
							</h5>
							<Button
								outline
								size="sm"
								iconClass="caret-right"
								title={translate('vehicleSearch.collapseItemDetailsPane', 'Collapse Item Details Pane')}
								onclick={() => (itemDetailVehicle = null)}
							/>
						</div>
					</div>
					<div
						class="card-body"
						style="overflow-y: auto; max-height: 80vh;"
					>
						<ItemDetail
							{asr}
							item={itemDetailVehicle}
							bind:additionalInfoType={itemDetailAdditionalInfoType}
						/>
					</div>
				</div>
			</div>
		{/if}
	</div>
</div>

<Modal
	confirmShown={false}
	title="Stock #: {itemDetailVehicle?.stockNumber ?? 'Item Details'}"
	subtitle={itemDetailManModSubtitle}
	show={!!(itemDetailVehicle && showItemDetailModal)}
	close={() => {
		showItemDetailModal = false
		itemDetailVehicle = null
	}}
	>{#if itemDetailVehicle}
		<ItemDetail
			{asr}
			item={itemDetailVehicle}
			bind:additionalInfoType={itemDetailAdditionalInfoType}
		/>
	{/if}</Modal
>
